import React, { useState } from 'react';
import { Box, Typography, Avatar, CircularProgress, IconButton, Alert } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GoogleButton } from 'react-google-button';
import { auth } from '../../firebase';
import { signInWithPopup, GoogleAuthProvider, signOut } from 'firebase/auth';
import { getRequest, postRequest } from '../../Fetch/request';
import { encryptUserId } from '../../Utils/encryption';

const provider = new GoogleAuthProvider();

const LoginOverlay = ({ onClose }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const resetToIdle = async ({ signOutFirebase = false } = {}) => {
    if (signOutFirebase) {
      try { await signOut(auth); } catch (_) { /* ignore */ }
    }
    setIsLoading(false);
    setLoadingMessage('');
  };

  const signInWithGoogle = async () => {
    setErrorMessage('');
    setIsLoading(true);
    setLoadingMessage('Connecting to Google...');

    let result;
    try {
      result = await signInWithPopup(auth, provider);
    } catch (error) {
      console.error('Google sign-in popup failed:', error?.code, error?.message);
      setErrorMessage(error?.message || 'Google sign-in was cancelled or failed.');
      await resetToIdle();
      return;
    }

    setLoadingMessage('Verifying your account...');
    const user = result.user;
    const encryptedUserId = encryptUserId(user.uid);

    try {
      await onlogin(user.email, encryptedUserId);
    } catch (error) {
      console.error('Backend login failed:', error);
      setErrorMessage(error?.message || 'Login failed. Please try again.');
      await resetToIdle({ signOutFirebase: true });
    }
  };

  const onlogin = async (email, encryptedUserId) => {

    setLoadingMessage('Setting up your account...');

    // Single API call to /user/login that handles everything
    const userObject = {
      name: "",
      email: email,
      id: encryptedUserId, // This is the encrypted Firebase UID
      user_id: encryptedUserId, // Backend expects user_id
      userId: encryptedUserId, // Also add userId for consistency
      avatar: null,
      designation: "",
      company: "",
      college: "",
      leetcode: "",
      cgpa: "",
      links: [
        {
          "name": "github",
          "tag": "personal",
          "link": ""
        },
        {
          "name": "linkedin",
          "tag": "personal",
          "link": ""
        },
        {
          "name": "twitter",
          "tag": "personal",
          "link": ""
        }
      ]
    };

    // Pass user_id in URL for backend - use encryptedUserId directly, not the default
    const loginUrl = `${process.env.REACT_APP_URL}/user/login?user_id=${encodeURIComponent(encryptedUserId)}`;
    console.log('[login] POST', loginUrl, 'body:', userObject);
    const loginData = await postRequest(loginUrl, userObject);
    console.log('[login] response:', loginData);

    if (loginData && loginData.data?.accessToken) {
      // Store everything in one go
      localStorage.setItem("email", email);
      localStorage.setItem("userId", encryptedUserId);
      localStorage.setItem("authToken", loginData.data.accessToken);
      
      if (loginData.data.refreshToken) {
        localStorage.setItem("refreshToken", loginData.data.refreshToken);
      } else {
        // Check if it's in a different location
        if (loginData.refreshToken) {
          localStorage.setItem("refreshToken", loginData.refreshToken);
        } else if (loginData.data?.data?.refreshToken) {
          localStorage.setItem("refreshToken", loginData.data.data.refreshToken);
        }
      }
      
      if (loginData.response) {
        localStorage.setItem("profileObject", JSON.stringify(loginData.response));
      }
      
      setLoadingMessage('Welcome! Redirecting you...');
      
      // Small delay to show the welcome message and ensure state is updated
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } else {
      localStorage.removeItem("email");
      localStorage.removeItem("profileObject");
      localStorage.removeItem("userId");
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");

      // Surface the backend's actual reason (e.g. EMAIL_NOT_VERIFIED) instead
      // of silently failing.
      const serverMsg = loginData?.error || loginData?.message
          || loginData?.data?.error || loginData?.data?.message;
      const serverCode = loginData?.code || loginData?.data?.code;

      // Persist the raw failure so it survives any subsequent reload — inspect
      // via `JSON.parse(localStorage.getItem('lastLoginError'))` in the console.
      try {
        localStorage.setItem('lastLoginError', JSON.stringify({
          at: new Date().toISOString(),
          email,
          response: loginData ?? null,
        }));
      } catch (_) { /* ignore quota errors */ }

      const friendly = serverCode === 'EMAIL_NOT_VERIFIED'
          ? 'Your Google account email isn\'t verified with Firebase yet. Verify it and try again.'
          : serverMsg || 'We couldn\'t complete sign-in. Please try again.';
      throw new Error(friendly);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
          maxWidth: '400px',
          width: '90%',
          position: 'relative'
        }}
      >
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#666'
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
        <Avatar 
          sx={{ 
            width: 120, 
            height: 120, 
            mb: 3,
            backgroundColor: 'transparent'
          }}
        >
          <img
            src="/images/logo_without_background.png"
            alt="slazysloth"
            style={{ 
              height: '100px', 
              width: '100px',
              objectFit: 'contain'
            }}
          />
        </Avatar>
        
        <Typography 
          variant="h4" 
          sx={{ 
            fontWeight: 'bold', 
            color: '#008080',
            mb: 1,
            textAlign: 'center',
            whiteSpace: 'nowrap'
          }}
        >
          Welcome to slazysloth
        </Typography>
        
        {!isLoading ? (
          <>
            <Typography
              variant="body1"
              sx={{
                color: '#666',
                mb: errorMessage ? 2 : 4,
                textAlign: 'center',
                lineHeight: 1.6
              }}
            >
              Practice DSA, System Design and Core Subjects with AI Feedback
            </Typography>

            {errorMessage && (
              <Alert
                severity="error"
                onClose={() => setErrorMessage('')}
                sx={{ mb: 2, width: '100%' }}
              >
                {errorMessage}
              </Alert>
            )}

            <Box
              sx={{
                borderRadius: '8px',
                display: 'inline-block',
                transition: 'transform 0.15s ease',
                '@keyframes blipPulse': {
                  '0%':   { boxShadow: '0 0 0 0 rgba(0, 128, 128, 0.55)' },
                  '40%':  { boxShadow: '0 0 0 14px rgba(0, 128, 128, 0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(0, 128, 128, 0)' },
                },
                animation: 'blipPulse 1.6s ease-out infinite',
                '& > div': {
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease !important',
                },
                '&:hover': {
                  animationPlayState: 'paused',
                  transform: 'translateY(-1px)',
                },
                '&:hover > div': {
                  backgroundColor: '#006666 !important',
                  boxShadow: '0 6px 18px rgba(0, 128, 128, 0.5) !important',
                },
                '&:active > div': {
                  backgroundColor: '#004d4d !important',
                  boxShadow: '0 2px 6px rgba(0, 128, 128, 0.4) !important',
                },
                '&:active': {
                  transform: 'translateY(1px)',
                },
                '&:focus-within > div': {
                  outline: '3px solid rgba(0, 128, 128, 0.35)',
                  outlineOffset: '2px',
                },
                '@media (prefers-reduced-motion: reduce)': {
                  animation: 'none',
                },
              }}
            >
              <GoogleButton
                type="dark"
                onClick={signInWithGoogle}
                style={{
                  borderRadius: '8px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25)',
                  backgroundColor: '#008080',
                  color: '#ffffff',
                  fontWeight: 600
                }}
              />
            </Box>
            
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#999',
                mt: 3,
                textAlign: 'center'
              }}
            >
              Sign in to access personalized practice questions, AI feedback, and track progress
            </Typography>
          </>
        ) : (
          <>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center',
              mb: 2
            }}>
              <CircularProgress 
                size={60}
                sx={{ 
                  color: '#008080',
                  mb: 3
                }}
              />
              <Typography 
                variant="h6" 
                sx={{ 
                  color: '#008080',
                  mb: 1,
                  textAlign: 'center',
                  fontWeight: 'bold'
                }}
              >
                {loadingMessage}
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: '#666',
                  textAlign: 'center',
                  lineHeight: 1.6
                }}
              >
                Please wait while we set up your account...
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default LoginOverlay; 